import { Box, Checkbox, Flex, FormControl, FormErrorMessage, FormHelperText } from "@chakra-ui/react";
import { useContext, useState } from "react";
import { useTranslation } from "react-i18next";
import { DateSelector } from "../../../../../../common/input/DateTimeSelectors/DateSelector";
import { usePendingSaves } from "../../../../../../common/input/pendingSaves";
import { useDebouncedSave } from "../../../../../../common/input/useDebouncedSave";
import { recheckHeldValuesAfter } from "../../../heldExtensionValues";
import { useAppDispatch } from "../../../../../../common/redux/hooks";
import { containsError } from "../../../../../../common/redux/reducers/contractReducer";
import { editContractThunk } from "../../../../../../common/redux/thunks/contract/edit-contract-thunk";
import { useContractState } from "../../../useContractState";
import { useDeadlineBeforeStartRefusal } from "./useDeadlineBeforeStartRefusal";
import { useEndDateOutsideExtensionsRefusal } from "./useEndDateOutsideExtensionsRefusal";
import { useLastSupportedDateRefusal } from "./useLastSupportedDateRefusal";
import { useWouldLapseExplanation } from "./useWouldLapseExplanation";
import { lapseAtEndDate, type LapseAtEndDate } from "../../../../options/unsavedOptionPeriods";
import { DatesContext } from "../../Details";
import { RemoveDataFieldButton } from "../../../../../../common/input/RemoveDataFieldButton";

interface Props {
  removeDataField?: () => Promise<void>;
  isRequiredButNotProvided: boolean;
}

export const ExpirationDate = ({ removeDataField, isRequiredButNotProvided }: Props) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const contractState = useContractState();
  const [heldLapse, setHeldLapse] = useState<LapseAtEndDate>();
  const deadlineRefusal = useDeadlineBeforeStartRefusal("EditExpirationDate");
  const lastDateRefusal = useLastSupportedDateRefusal("EditExpirationDate");
  const lapseExplanation = useWouldLapseExplanation("EditExpirationDate", heldLapse);
  const extensionsRefusal = useEndDateOutsideExtensionsRefusal();
  const errorMessage =
    lapseExplanation ??
    lastDateRefusal ??
    extensionsRefusal ??
    deadlineRefusal ??
    (containsError(contractState, "EditExpirationDate") ? t("Update failed") : null);
  const [hasNoExpirationDate, setHasNoExpirationDate] = useState<boolean>(
    () => contractState.contract.dataFields.endDate?.hasNoEndDate ?? false
  );

  const { expirationDate, setExpirationDate } = useContext(DatesContext);

  const pendingSaves = usePendingSaves();
  const handleDateChange = useDebouncedSave(async (date: string | undefined) => {
    const saved = await dispatch(
      editContractThunk({ command: { type: "EditExpirationDate", value: { date, hasNoExpirationDate: false } } })
    );
    await recheckHeldValuesAfter(pendingSaves, saved);
  });

  return (
    <FormControl id={"expirationDate"} isInvalid={errorMessage !== null} isRequired={isRequiredButNotProvided}>
      <Box fontWeight="medium" fontSize="sm" pb="2">
        {t("End date")}
      </Box>

      <Flex flexDirection={"row"} justifyContent={"space-between"}>
        <Flex>
          <Flex width="300px">
            <DateSelector
              isDisabled={hasNoExpirationDate}
              date={hasNoExpirationDate ? null : expirationDate}
              onChange={(date) => {
                setExpirationDate(date ?? null);
                const lapse = lapseAtEndDate(contractState.contract.dataFields.options ?? [], date);
                setHeldLapse(lapse);
                if (lapse) {
                  handleDateChange.cancel();
                  return;
                }
                handleDateChange(date);
              }}
            />
          </Flex>
          <Checkbox
            ml="2.5"
            isChecked={hasNoExpirationDate}
            onChange={async (e) => {
              const isChecked = e.target.checked;
              const before = { hasNoExpirationDate, expirationDate };
              setHasNoExpirationDate(isChecked);
              setHeldLapse(undefined);
              if (isChecked) setExpirationDate(null);
              const result = await dispatch(
                editContractThunk({
                  command: {
                    type: "EditExpirationDate",
                    value: {
                      date: isChecked ? undefined : expirationDate ?? undefined,
                      hasNoExpirationDate: isChecked,
                    },
                  },
                })
              );
              const refused =
                result.meta.requestStatus === "rejected" ||
                (result.payload as { errors?: string[] } | undefined)?.errors?.includes("EditExpirationDate");
              if (refused) {
                setHasNoExpirationDate(before.hasNoExpirationDate);
                setExpirationDate(before.expirationDate);
              }
              await recheckHeldValuesAfter(pendingSaves, result);
            }}
          >
            {t("Contract has no end date")}
          </Checkbox>
        </Flex>
        <RemoveDataFieldButton removeDataField={removeDataField} />
      </Flex>
      <FormHelperText>{t("Provide an end date")}</FormHelperText>
      <FormErrorMessage>{errorMessage}</FormErrorMessage>
    </FormControl>
  );
};
